import './analysis/analysis.css';
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Chart } from "react-google-charts";
import { useLoginCheck } from "../../hooks/useLoginCheck";
import "../../styles/scss/metaAds.scss";

// 数値フォーマット
const formatNumber = (num) => {
  if (num == null || !Number.isFinite(Number(num))) return "未取得";
  const n = Number(num);
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
};

const formatCurrency = (num) => num == null ? "—" : `¥${Math.round(Number(num)).toLocaleString()}`;
const formatCV = (num) => num == null ? "未取得" : Number(num).toLocaleString();
const formatRate = (num) => num == null ? "—" : `${Number(num).toFixed(2)}%`;
const EMPTY_DAYS = [];

const STATUS_LABELS = {
  ACTIVE: { label: "配信中", className: "status-active" },
  PAUSED: { label: "停止中", className: "status-paused" },
  ARCHIVED: { label: "アーカイブ", className: "status-archived" },
  DELETED: { label: "削除済み", className: "status-archived" },
};

const OBJECTIVE_LABELS = {
  CONVERSIONS: "コンバージョン",
  OUTCOME_SALES: "売上",
  OUTCOME_LEADS: "リード",
  OUTCOME_TRAFFIC: "トラフィック",
  OUTCOME_AWARENESS: "認知度",
  OUTCOME_ENGAGEMENT: "エンゲージメント",
  OUTCOME_APP_PROMOTION: "アプリ促進",
  REACH: "リーチ",
  TRAFFIC: "トラフィック",
  ENGAGEMENT: "エンゲージメント",
  AWARENESS: "認知度",
  LEAD_GENERATION: "リード獲得",
  MESSAGES: "メッセージ",
  VIDEO_VIEWS: "動画再生",
};

const CREATIVE_SORT_OPTIONS = [
  { value: "spend", label: "広告費" },
  { value: "impressions", label: "インプレッション" },
  { value: "clicks", label: "リンククリック" },
  { value: "conversions", label: "ウェブ申込CV" },
  { value: "ctr", label: "CTR" },
  { value: "cpc", label: "CPC" },
  { value: "roas", label: "ROAS" },
];


function MetaAdsPage() {
  useLoginCheck();
  const [selectedDays, setSelectedDays] = useState(30);
  const generation = useRef(0);
  const [scope, setScope] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [campaignFilter, setCampaignFilter] = useState("ALL");

  const [campaigns, setCampaigns] = useState([]);
  const [dailyData, setDailyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // クリエイティブ展開状態: { [campaignId]: { open, loading, data, error, sortBy } }
  const [creativeStates, setCreativeStates] = useState({});

  // ===== API呼び出し =====
  const fetchDaily = useCallback(async (days) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const response = await fetch(`${apiUrl}/api/get_meta_ads_daily_data/?days=${days}&campaign_id=${scope}`, {
      method: "GET",
      cache: "no-store",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "日別データの取得に失敗しました");
    }
    if (data.conversion_definition?.version !== "website_lead_v2" || data.report_status !== "complete") {
      throw new Error("日別集計が未取得または旧定義です。再取得してください。");
    }
    return data;
  }, [scope]);

  const loadAll = useCallback(
    async (days) => {
      const requestId = ++generation.current;
      setLoading(true);
      setError(null);
      setCreativeStates({});
      try {
        const dailyRes = await fetchDaily(days);
        if (requestId !== generation.current) return;
        setCampaigns(dailyRes.campaigns || []);
        setDailyData(dailyRes);
      } catch (err) {
        if (requestId !== generation.current) return;
        console.error(err);
        setCampaigns([]);
        setDailyData(null);
        setError(err.message || "データの取得に失敗しました");
      } finally {
        if (requestId === generation.current) setLoading(false);
      }
    },
    [fetchDaily]
  );

  useEffect(() => {
    loadAll(selectedDays);
    return () => { generation.current += 1; };
  }, [selectedDays, loadAll]);

  // 期間変更時はクリエイティブのキャッシュをクリア（取得済みのものを再取得させる）
  useEffect(() => {
    setCreativeStates({});
  }, [selectedDays]);

  const fetchCreatives = useCallback(
    async (campaignId) => {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/get_meta_ads_creatives/?campaign_id=${campaignId}&days=${selectedDays}`,
        { method: "GET", credentials: "include", cache: "no-store" }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "クリエイティブの取得に失敗しました");
      }
      if (!Array.isArray(data) || data.some((ad) => ad.insights?.conversion_definition?.version !== "website_lead_v2")) {
        throw new Error("広告のCV集計定義を確認できません。");
      }
      return data;
    },
    [selectedDays]
  );

  const toggleCreatives = useCallback(
    async (campaignId) => {
      setCreativeStates((prev) => {
        const current = prev[campaignId];
        // 既に開いてデータあり → 閉じる
        if (current?.open && current?.data) {
          return { ...prev, [campaignId]: { ...current, open: false } };
        }
        // 既にデータあり、閉じている → 開く
        if (current?.data) {
          return { ...prev, [campaignId]: { ...current, open: true } };
        }
        // 初回 → ロード開始
        return {
          ...prev,
          [campaignId]: { open: true, loading: true, sortBy: "spend" },
        };
      });

      const existing = creativeStates[campaignId];
      if (existing?.data) return;

      const requestId = generation.current;
      try {
        const data = await fetchCreatives(campaignId);
        if (requestId !== generation.current) return;
        setCreativeStates((prev) => ({
          ...prev,
          [campaignId]: {
            open: true,
            loading: false,
            data,
            sortBy: prev[campaignId]?.sortBy || "spend",
          },
        }));
      } catch (err) {
        if (requestId !== generation.current) return;
        setCreativeStates((prev) => ({
          ...prev,
          [campaignId]: {
            open: true,
            loading: false,
            error: err.message,
            sortBy: "spend",
          },
        }));
      }
    },
    [creativeStates, fetchCreatives]
  );

  const setCreativeSort = useCallback((campaignId, sortBy) => {
    setCreativeStates((prev) => ({
      ...prev,
      [campaignId]: { ...prev[campaignId], sortBy },
    }));
  }, []);

  // ===== 派生データ =====
  const summary = dailyData?.summary || null;
  const dailyBreakdown = dailyData?.daily_breakdown || EMPTY_DAYS;

  const filteredCampaigns = useMemo(() => {
    if (campaignFilter === "ALL") return campaigns;
    return campaigns.filter((c) => c.status === campaignFilter);
  }, [campaigns, campaignFilter]);

  // チャートデータ
  const trendChartData = useMemo(() => {
    const header = ["日付", "広告費", "ウェブ申込CV"];
    const rows = dailyBreakdown.map((d) => [
      new Date(`${d.date}T12:00:00`).toLocaleDateString("ja-JP", {
        month: "numeric",
        day: "numeric",
      }),
      d.spend,
      d.conversions,
    ]);
    return [header, ...rows];
  }, [dailyBreakdown]);

  const reachChartData = useMemo(() => {
    const header = ["日付", "インプレッション", "クリック"];
    const rows = dailyBreakdown.map((d) => [
      new Date(`${d.date}T12:00:00`).toLocaleDateString("ja-JP", {
        month: "numeric",
        day: "numeric",
      }),
      d.impressions,
      d.clicks,
    ]);
    return [header, ...rows];
  }, [dailyBreakdown]);

  const campaignSpendData = useMemo(() => {
    const header = ["キャンペーン", "広告費"];
    const rows = campaigns
      .filter((c) => (c.insights?.spend || 0) > 0)
      .map((c) => [c.name, c.insights.spend]);
    return [header, ...rows];
  }, [campaigns]);

  const dailyCoverage = dailyBreakdown.filter(d => d.spend != null && d.impressions != null && d.clicks != null && d.conversions != null).length;
  const kpiCards = summary
    ? [
        {
          key: "spend",
          label: "広告費",
          icon: "fa-yen-sign",
          value: formatCurrency(summary.spend),
          color: "purple",
        },
        {
          key: "impressions",
          label: "インプレッション",
          icon: "fa-eye",
          value: formatNumber(summary.impressions),
          color: "blue",
        },
        {
          key: "clicks",
          label: "リンククリック",
          icon: "fa-arrow-pointer",
          value: formatNumber(summary.clicks),
          color: "cyan",
        },
        {
          key: "conversions",
          label: "ウェブ申込CV",
          icon: "fa-bullseye",
          value: formatCV(summary.conversions),
          color: "green",
        },
        {
          key: "ctr",
          label: "CTR",
          icon: "fa-percent",
          value: formatRate(summary.ctr),
          color: "orange",
        },
        {
          key: "cpc",
          label: "CPC",
          icon: "fa-coins",
          value: formatCurrency(summary.cpc),
          color: "pink",
        },
        {
          key: "cpa",
          label: "申込CPA",
          icon: "fa-tag",
          value: formatCurrency(summary.cpa),
          color: "red",
        },
        {
          key: "cvr",
          label: "申込CV率（リンククリック比）",
          icon: "fa-chart-line",
          value: formatRate(summary.conversion_rate),
          color: "teal",
        },
      ]
    : [];

  return (
    <div className="meta-ads-page">
      {/* ヘッダー */}
      <div className="meta-ads-header">
        <div className="header-title">
          <h2>
            <i className="fa-brands fa-meta"></i>
            Meta広告ダッシュボード
          </h2>
          <p className="subtitle">
            Facebook / Instagram広告のパフォーマンスを集計・分析
          </p>
        </div>

        <div className="header-actions">
          <label>対象 <select aria-label="集計対象" value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="">全対象キャンペーン</option>
            <option value="52578886220640">木津川キャンペーン</option>
          </select></label>
          <div className="period-selector">
            {[1, 5, 7, 14, 30, 90].map((days) => (
              <button
                key={days}
                className={`period-btn ${selectedDays === days ? "active" : ""}`}
                onClick={() => setSelectedDays(days)}
              >
                {days === 1 ? "昨日" : `${days}日`}
              </button>
            ))}
          </div>
          <button
            className="refresh-btn"
            onClick={() => loadAll(selectedDays)}
            title="再取得"
          >
            <i className="fa-solid fa-rotate"></i>
          </button>
        </div>
      </div>

      <div className="conversion-definition" role="note">
        <strong>ウェブ申込CV：ウェブサイトのLeadのみ</strong>
        <p>体験申込で送信するLeadを対象にし、購入・登録・包括的なleadは合算しません。
          過去の表示も同じ定義で再集計されます。実申込人数とは異なります。</p>
        <p>CV率は全リンククリック比です。未取得のCVは「未取得」、算出できないCPA・CV率は「—」。
          日別Leadの未返却は0と断定しません。期間CVは同条件の期間API明示値を使用し、日別の部分合計ではありません。
          広告アカウントの時間帯で昨日までを集計します。直近の値は後日更新される場合があります。</p>
        {summary && <p>期間のウェブ申込CV：{formatCV(summary.conversions)}</p>}
        {dailyData && <p>{dailyData.period?.since} 〜 {dailyData.period?.until}（{dailyData.account_timezone}）／通貨：{dailyData.currency}／取得（UTC）：{dailyData.retrieved_at}
          ／帰属：クリック7日・表示1日、広告表示日基準。地域等の広告設定は変更しません。</p>}
      </div>

      {/* タブ */}
      <div className="meta-tabs">
        <button
          className={`tab ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          <i className="fa-solid fa-chart-pie"></i>
          概要
        </button>
        <button
          className={`tab ${activeTab === "campaigns" ? "active" : ""}`}
          onClick={() => setActiveTab("campaigns")}
        >
          <i className="fa-solid fa-bullhorn"></i>
          キャンペーン
        </button>
        <button
          className={`tab ${activeTab === "daily" ? "active" : ""}`}
          onClick={() => setActiveTab("daily")}
        >
          <i className="fa-solid fa-calendar-days"></i>
          日別データ
        </button>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="error-banner">
          <i className="fa-solid fa-triangle-exclamation"></i>
          <div>
            <p className="error-title">データを取得できませんでした</p>
            <p className="error-detail">{error}</p>
          </div>
          <button onClick={() => loadAll(selectedDays)}>
            <i className="fa-solid fa-rotate"></i>
            再試行
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading-skeleton">
          <div className="skeleton-row" />
          <div className="skeleton-row" />
          <div className="skeleton-row" />
        </div>
      ) : error ? null : (
        <>
          {/* ===== 概要タブ ===== */}
          {activeTab === "overview" && (
            <div className="tab-content">
              {kpiCards.length > 0 ? (
                <>
                  <div className="kpi-grid kpi-primary">
                    {kpiCards.filter(k=>["spend","clicks","conversions","cpa"].includes(k.key)).map((kpi) => (
                      <div key={kpi.key} className={`kpi-card kpi-${kpi.color}`}>
                        <div className="kpi-icon">
                          <i className={`fa-solid ${kpi.icon}`}></i>
                        </div>
                        <div className="kpi-body">
                          <p className="kpi-label">{kpi.label}</p>
                          <p className="kpi-value">{kpi.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
<p role="status">期間合計と日別の取得状態は別です。日別主要指標がそろった日：{dailyCoverage} / {dailyBreakdown.length}日。未取得は0で補わず、欠損部分の線をつなぎません。</p>
<details className="analysis-detail"><summary>表示回数・率・クリック単価</summary>                  <div className="kpi-grid">
                    {kpiCards.filter(k=>!["spend","clicks","conversions","cpa"].includes(k.key)).map((kpi) => (
                      <div key={kpi.key} className={`kpi-card kpi-${kpi.color}`}>
                        <div className="kpi-icon">
                          <i className={`fa-solid ${kpi.icon}`}></i>
                        </div>
                        <div className="kpi-body">
                          <p className="kpi-label">{kpi.label}</p>
                          <p className="kpi-value">{kpi.value}</p>
                        </div>
                      </div>
                    ))}
                  </div></details>

                  <div className="charts-grid">
                    <div className="chart-card chart-wide">
                      <div className="chart-header">
                        <h3>
                          <i className="fa-solid fa-chart-area"></i>
                          広告費 & ウェブ申込CV推移
                        </h3>
                      </div>
                      {trendChartData.length > 1 ? (
                        <Chart
                          chartType="AreaChart"
                          width="100%"
                          height="320px"
                          data={trendChartData}
                          options={{
                            backgroundColor: "transparent",
                            chartArea: {
                              left: 60,
                              top: 20,
                              right: 60,
                              bottom: 50,
                              width: "85%",
                              height: "75%",
                            },
                            colors: ["#a78bfa", "#34d399"],
                            legend: {
                              position: "top",
                              alignment: "end",
                              textStyle: { color: "#cbd5e1", fontSize: 12 },
                            },
                            hAxis: {
                              textStyle: { fontSize: 11, color: "#94a3b8" },
                              gridlines: { color: "transparent" },
                            },
                            vAxes: {
                              0: {
                                textStyle: { fontSize: 11, color: "#a78bfa" },
                                gridlines: { color: "rgba(148, 163, 184, 0.08)" },
                                baselineColor: "rgba(148, 163, 184, 0.15)",
                              },
                              1: {
                                textStyle: { fontSize: 11, color: "#34d399" },
                                gridlines: { color: "transparent" },
                                baselineColor: "transparent",
                              },
                            },
                            series: {
                              0: { targetAxisIndex: 0, areaOpacity: 0.25 },
                              1: { targetAxisIndex: 1, areaOpacity: 0.25 },
                            },
                          }}
                        />
                      ) : (
                        <p className="empty-msg">データがありません</p>
                      )}
                    </div>

                    <div className="chart-card">
                      <div className="chart-header">
                        <h3>
                          <i className="fa-solid fa-chart-pie"></i>
                          キャンペーン別広告費
                        </h3>
                      </div>
                      {campaignSpendData.length > 1 ? (
                        <Chart
                          chartType="PieChart"
                          width="100%"
                          height="320px"
                          data={campaignSpendData}
                          options={{
                            backgroundColor: "transparent",
                            chartArea: {
                              left: 20,
                              top: 20,
                              width: "100%",
                              height: "80%",
                            },
                            colors: [
                              "#a78bfa",
                              "#f472b6",
                              "#60a5fa",
                              "#34d399",
                              "#fb923c",
                              "#22d3ee",
                              "#facc15",
                              "#fb7185",
                            ],
                            legend: {
                              position: "right",
                              textStyle: { fontSize: 11, color: "#cbd5e1" },
                            },
                            pieHole: 0.5,
                            pieSliceText: "percentage",
                            pieSliceTextStyle: { fontSize: 11, color: "#fff" },
                            pieSliceBorderColor: "transparent",
                          }}
                        />
                      ) : (
                        <p className="empty-msg">データがありません</p>
                      )}
                    </div>

                    <div className="chart-card chart-wide">
                      <div className="chart-header">
                        <h3>
                          <i className="fa-solid fa-chart-line"></i>
                          インプレッション & クリック推移
                        </h3>
                      </div>
                      {reachChartData.length > 1 ? (
                        <Chart
                          chartType="LineChart"
                          width="100%"
                          height="280px"
                          data={reachChartData}
                          options={{
                            backgroundColor: "transparent",
                            chartArea: {
                              left: 70,
                              top: 20,
                              right: 70,
                              bottom: 50,
                              width: "85%",
                              height: "75%",
                            },
                            colors: ["#60a5fa", "#f472b6"],
                            legend: {
                              position: "top",
                              alignment: "end",
                              textStyle: { color: "#cbd5e1", fontSize: 12 },
                            },
                            curveType: "function",
                            hAxis: {
                              textStyle: { fontSize: 11, color: "#94a3b8" },
                              gridlines: { color: "transparent" },
                            },
                            vAxes: {
                              0: {
                                textStyle: { fontSize: 11, color: "#60a5fa" },
                                gridlines: { color: "rgba(148, 163, 184, 0.08)" },
                                baselineColor: "rgba(148, 163, 184, 0.15)",
                              },
                              1: {
                                textStyle: { fontSize: 11, color: "#f472b6" },
                                gridlines: { color: "transparent" },
                                baselineColor: "transparent",
                              },
                            },
                            series: {
                              0: { targetAxisIndex: 0, lineWidth: 3 },
                              1: { targetAxisIndex: 1, lineWidth: 3 },
                            },
                          }}
                        />
                      ) : (
                        <p className="empty-msg">データがありません</p>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <p className="empty-msg">データがありません</p>
              )}
            </div>
          )}

          {/* ===== キャンペーンタブ ===== */}
          {activeTab === "campaigns" && (
            <div className="tab-content">
              <div className="campaign-filter">
                {[
                  { value: "ALL", label: "すべて" },
                  { value: "ACTIVE", label: "配信中" },
                  { value: "PAUSED", label: "停止中" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    className={`filter-chip ${
                      campaignFilter === opt.value ? "active" : ""
                    }`}
                    onClick={() => setCampaignFilter(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {filteredCampaigns.length > 0 ? (
                <div className="campaigns-grid">
                  {filteredCampaigns.map((campaign) => {
                    const status =
                      STATUS_LABELS[campaign.status] || STATUS_LABELS.ARCHIVED;
                    const insights = campaign.insights || {};
                    return (
                      <div key={campaign.id} className="campaign-card">
                        <div className="campaign-card-header">
                          <div className="campaign-name">
                            <h4>{campaign.name}</h4>
                            <p className="campaign-meta">
                              <span className={`status-badge ${status.className}`}>
                                <span className="status-dot"></span>
                                {status.label}
                              </span>
                              {campaign.objective && (
                                <span className="objective-badge">
                                  {OBJECTIVE_LABELS[campaign.objective] ||
                                    campaign.objective}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="campaign-budget">
                            <p className="budget-label">日予算</p>
                            <p className="budget-value">
                              {campaign.daily_budget
                                ? formatCurrency(campaign.daily_budget)
                                : "-"}
                            </p>
                          </div>
                        </div>

                        <div className="campaign-period">
                          <i className="fa-solid fa-calendar"></i>
                          {campaign.start_time
                            ? new Date(campaign.start_time).toLocaleDateString(
                                "ja-JP"
                              )
                            : "-"}{" "}
                          〜{" "}
                          {campaign.stop_time
                            ? new Date(campaign.stop_time).toLocaleDateString(
                                "ja-JP"
                              )
                            : "継続中"}
                        </div>

                        <div className="campaign-metrics">
                          <div className="metric">
                            <p className="metric-label">広告費</p>
                            <p className="metric-value">
                              {formatCurrency(insights.spend)}
                            </p>
                          </div>
                          <div className="metric">
                            <p className="metric-label">インプレッション</p>
                            <p className="metric-value">
                              {formatNumber(insights.impressions)}
                            </p>
                          </div>
                          <div className="metric">
                            <p className="metric-label">クリック</p>
                            <p className="metric-value">
                              {formatNumber(insights.clicks)}
                            </p>
                          </div>
                          <div className="metric">
                            <p className="metric-label">ウェブ申込CV</p>
                            <p className="metric-value highlight">
                              {formatCV(insights.conversions)}
                            </p>
                          </div>
                          <div className="metric">
                            <p className="metric-label">CTR</p>
                            <p className="metric-value">
                              {formatRate(insights.ctr)}
                            </p>
                          </div>
                          <div className="metric">
                            <p className="metric-label">CPC</p>
                            <p className="metric-value">
                              {formatCurrency(insights.cpc)}
                            </p>
                          </div>
                          <div className="metric">
                            <p className="metric-label">CV率（リンククリック比）</p>
                            <p className="metric-value">
                              {formatRate(insights.conversion_rate)}
                            </p>
                          </div>
                          <div className="metric">
                            <p className="metric-label">申込CPA</p>
                            <p className="metric-value">{formatCurrency(insights.cpa)}</p>
                          </div>
                          <div className="metric">
                            <p className="metric-label">ROAS</p>
                            <p
                              className={`metric-value ${
                                insights.roas >= 3
                                  ? "good"
                                  : insights.roas >= 2
                                  ? "ok"
                                  : insights.roas > 0
                                  ? "warn"
                                  : ""
                              }`}
                            >
                              {insights.roas ? `${insights.roas}x` : "-"}
                            </p>
                          </div>
                        </div>

                        {/* クリエイティブ展開 */}
                        <CreativeSection
                          campaignId={campaign.id}
                          state={creativeStates[campaign.id]}
                          onToggle={toggleCreatives}
                          onSortChange={setCreativeSort}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="empty-msg">キャンペーンがありません</p>
              )}
            </div>
          )}

          {/* ===== 日別データタブ ===== */}
          {activeTab === "daily" && (
            <div className="tab-content">
              {dailyBreakdown.length > 0 && summary ? (
                <div className="daily-table-wrapper">
                  <table className="daily-table">
                    <thead>
                      <tr>
                        <th>日付</th>
                        <th>広告費</th>
                        <th>インプレッション</th>
                        <th>リンククリック</th>
                        <th>ウェブ申込CV</th>
                        <th>CTR</th>
                        <th>CPC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...dailyBreakdown].reverse().map((day) => {
                        const date = new Date(`${day.date}T12:00:00`);
                        const isWeekend =
                          date.getDay() === 0 || date.getDay() === 6;
                        return (
                          <tr key={day.date} className={isWeekend ? "weekend" : ""}>
                            <td className="date-cell">
                              {day.conversion_status === "insights_unavailable" && <small className="activity-note">実績未取得</small>}
                              <span className="date-main">
                                {date.toLocaleDateString("ja-JP", {
                                  month: "numeric",
                                  day: "numeric",
                                })}
                              </span>
                              <span className="date-sub">
                                {
                                  ["日", "月", "火", "水", "木", "金", "土"][
                                    date.getDay()
                                  ]
                                }
                              </span>
                            </td>
                            <td>{formatCurrency(day.spend)}</td>
                            <td>{formatNumber(day.impressions)}</td>
                            <td>{formatNumber(day.clicks)}</td>
                            <td className="cv-cell">{formatCV(day.conversions)}</td>
                            <td>{formatRate(day.ctr)}</td>
                            <td>{formatCurrency(day.cpc)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>期間API集計</td>
                        <td>{formatCurrency(summary.spend)}</td>
                        <td>{formatNumber(summary.impressions)}</td>
                        <td>{formatNumber(summary.clicks)}</td>
                        <td>{formatCV(summary.conversions)}</td>
                        <td>{formatRate(summary.ctr)}</td>
                        <td>{formatCurrency(summary.cpc)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="empty-msg">日別データがありません</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ===== クリエイティブセクション =====
function CreativeSection({ campaignId, state, onToggle, onSortChange }) {
  const isOpen = !!state?.open;
  const sortBy = state?.sortBy || "spend";

  const sortedCreatives = useMemo(() => {
    if (!state?.data) return [];
    return [...state.data].sort((a, b) => {
      const av = a.insights?.[sortBy] || 0;
      const bv = b.insights?.[sortBy] || 0;
      // CPCは小さい方が良いので昇順
      if (sortBy === "cpc") return av - bv;
      return bv - av;
    });
  }, [state?.data, sortBy]);

  const maxSpend = useMemo(() => {
    if (!sortedCreatives.length) return 0;
    return Math.max(...sortedCreatives.map((c) => c.insights?.spend || 0));
  }, [sortedCreatives]);

  return (
    <div className="creative-section">
      <button
        className={`creative-toggle ${isOpen ? "open" : ""}`}
        onClick={() => onToggle(campaignId)}
      >
        <i className="fa-solid fa-images"></i>
        <span>クリエイティブ別パフォーマンス</span>
        {state?.data && (
          <span className="creative-count">{state.data.length}</span>
        )}
        <i
          className={`fa-solid fa-chevron-down chevron ${isOpen ? "rotate" : ""}`}
        ></i>
      </button>

      {isOpen && (
        <div className="creative-body">
          {state?.loading ? (
            <div className="creative-loading">
              <div className="skeleton-row" />
              <div className="skeleton-row" />
            </div>
          ) : state?.error ? (
            <div className="creative-error">
              <i className="fa-solid fa-triangle-exclamation"></i>
              {state.error}
            </div>
          ) : sortedCreatives.length === 0 ? (
            <p className="empty-msg">クリエイティブがありません</p>
          ) : (
            <>
              <div className="creative-sort-bar">
                <span className="sort-label">
                  <i className="fa-solid fa-sort"></i>
                  並び替え:
                </span>
                {CREATIVE_SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    className={`sort-chip ${sortBy === opt.value ? "active" : ""}`}
                    onClick={() => onSortChange(campaignId, opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="creatives-grid">
                {sortedCreatives.map((ad, index) => {
                  const insights = ad.insights || {};
                  const creative = ad.creative || {};
                  const status =
                    STATUS_LABELS[ad.status] || STATUS_LABELS.ARCHIVED;
                  const spendRatio =
                    maxSpend > 0 ? (insights.spend / maxSpend) * 100 : 0;

                  return (
                    <div key={ad.id} className="creative-card">
                      <div className="creative-thumb">
                        {creative.thumbnail_url || creative.image_url ? (
                          <img
                            src={creative.thumbnail_url || creative.image_url}
                            alt={ad.name}
                            loading="lazy"
                          />
                        ) : creative.video_id ? (
                          <div className="creative-placeholder video">
                            <i className="fa-solid fa-circle-play"></i>
                          </div>
                        ) : (
                          <div className="creative-placeholder">
                            <i className="fa-solid fa-image"></i>
                          </div>
                        )}
                        <span className="creative-rank">#{index + 1}</span>
                        <span
                          className={`creative-status ${status.className}`}
                        >
                          <span className="status-dot"></span>
                          {status.label}
                        </span>
                      </div>

                      <div className="creative-content">
                        <div className="creative-info">
                          <p className="creative-name" title={ad.name}>
                            {ad.name}
                          </p>
                          {creative.title && (
                            <p className="creative-title">{creative.title}</p>
                          )}
                          {creative.body && (
                            <p className="creative-body-text">
                              {creative.body}
                            </p>
                          )}
                        </div>

                        <div className="creative-spend-bar">
                          <div
                            className="bar-fill"
                            style={{ width: `${spendRatio}%` }}
                          ></div>
                        </div>

                        <div className="creative-metrics">
                          <div className="c-metric">
                            <span className="c-label">広告費</span>
                            <span className="c-value">
                              {formatCurrency(insights.spend)}
                            </span>
                          </div>
                          <div className="c-metric">
                            <span className="c-label">IMP</span>
                            <span className="c-value">
                              {formatNumber(insights.impressions)}
                            </span>
                          </div>
                          <div className="c-metric">
                            <span className="c-label">CLK</span>
                            <span className="c-value">
                              {formatNumber(insights.clicks)}
                            </span>
                          </div>
                          <div className="c-metric">
                            <span className="c-label">ウェブ申込CV</span>
                            <span className="c-value highlight">
                              {formatCV(insights.conversions)}
                            </span>
                          </div>
                          <div className="c-metric">
                            <span className="c-label">CTR</span>
                            <span className="c-value">
                              {formatRate(insights.ctr)}
                            </span>
                          </div>
                          <div className="c-metric">
                            <span className="c-label">CPC</span>
                            <span className="c-value">
                              {formatCurrency(insights.cpc)}
                            </span>
                          </div>
                          <div className="c-metric">
                            <span className="c-label">CV率（リンククリック比）</span>
                            <span className="c-value">
                              {formatRate(insights.conversion_rate)}
                            </span>
                          </div>
                          <div className="c-metric">
                            <span className="c-label">申込CPA</span>
                            <span className="c-value">{formatCurrency(insights.cpa)}</span>
                          </div>
                          <div className="c-metric">
                            <span className="c-label">ROAS</span>
                            <span
                              className={`c-value ${
                                insights.roas >= 3
                                  ? "good"
                                  : insights.roas >= 2
                                  ? "ok"
                                  : insights.roas > 0
                                  ? "warn"
                                  : ""
                              }`}
                            >
                              {insights.roas ? `${insights.roas}x` : "-"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default MetaAdsPage;
